import createWorker from "./createWorker.js";
export default function createWorkerFunction_(createWorkerInEnvironment, fnOrAlias, createMethods, createInputHandler, handleError, handleOutput) {
    // "Started" and "stopped" flags.
    let started = false;
    let stopped = false;
    // "Closure" functions that return any external dependencies.
    let getDependenciesFunctions = [];
    // `transferList` for external dependencies.
    const dependenciesTransferList = undefined;
    let inputTransferList = () => [];
    let outputTransferList = () => [];
    // (optional) Caching.
    let alias = undefined;
    let cacheValue = undefined;
    const getFromCache = (cacheKey) => {
        return CACHE[cacheKey];
    };
    const setInCache = (cacheKey, value) => {
        CACHE[cacheKey] = value;
    };
    const mustHaveStarted = () => {
        if (!started) {
            throw new Error('Not started');
        }
    };
    const mustNotHaveStarted = () => {
        if (started) {
            throw new Error('Was started');
        }
    };
    const mustNotHaveStopped = () => {
        if (stopped) {
            throw new Error('Was stopped');
        }
    };
    const mustNotHaveAlias = () => {
        if (alias) {
            throw new Error('Has alias');
        }
    };
    const argumentMustBeFunction = (arg) => {
        if (typeof arg !== 'function') {
            throw new TypeError('Argument must be a function');
        }
    };
    let fn;
    // See whether the argument is an alias string or a function.
    if (typeof fnOrAlias === 'string') {
        alias = fnOrAlias;
        cacheValue = getFromCache(alias);
        // The additional `if` conditions were added just to work around
        // TypeScript compiler error message.
        if (!cacheValue || !cacheValue.$) {
            throw new Error('Not found');
        }
        // Restore the functions from cache.
        fn = cacheValue.$[0];
        getDependenciesFunctions = cacheValue.$[1];
        inputTransferList = cacheValue.$[2];
        outputTransferList = cacheValue.$[3];
        // The "expect error" guard below checks that a developer of this package
        // didn't "forget" to restore any other stuff from cache.
        // @ts-expect-error: All elements of the `cacheValue` array should be restored above. In case of adding new stuff in `cachedValue`, increment the index below accordingly.
        cacheValue.$[4];
    }
    else {
        fn = fnOrAlias;
    }
    // Validate the function.
    argumentMustBeFunction(fn);
    let worker;
    /**
     * Adds external dependencies.
     * These dependencies must not change after the function is started.
     *
     * @param {function} getDependencies — A "closure" function that returns an array of dependencies — global variables or functions — that will be used in this worker. If some dependencies get overlooked, the worker will throw "[name] is not defined".
     */
    const addDependencies_ = (getDependencies) => {
        mustNotHaveStopped();
        mustNotHaveStarted();
        // Non-TypeScript code argument validation.
        argumentMustBeFunction(getDependencies);
        getDependenciesFunctions.push(getDependencies);
    };
    // Starts the worker.
    const start = () => {
        mustNotHaveStopped();
        mustNotHaveStarted();
        // These dependencies can change before the function is started, but not after that.
        //
        // `createInputHandler()` is not supplied by the user, and it stays the same.
        // It's the same between any two "streaming" or non-"streaming" worker functions.
        //
        const getInitialDependencies = () => [
            fn,
            outputTransferList,
            createInputHandler
        ];
        addDependencies_(getInitialDependencies);
        started = true;
        worker.start(getDependenciesFunctions, dependenciesTransferList);
    };
    // Stops the worker.
    // Calling this function multiple times is normal and it won't produce any errors.
    const stop = () => {
        stopped = true;
        worker.stop();
    };
    // Sends input data to the worker.
    const sendToWorker = (inputArgs) => {
        worker.ingest([Date.now(), inputArgs], inputTransferList(...inputArgs));
    };
    // These two variables are defined here just to work around
    // TypeScript compiler error message.
    let inputLatency;
    let outputLatency;
    // Declare a worker function. It will be returned from this function.
    const workerFn = Object.assign({ inputLatency,
        outputLatency,
        /**
         * Adds external dependencies.
         * These dependencies must not change after the function is started.
         *
         * @param {function} getDependencies — A "closure" function that returns an array of dependencies — global variables or functions — that will be used in this worker. If some dependencies get overlooked, the worker will throw "[name] is not defined".
         */
        addDependencies(getDependencies) {
            mustNotHaveAlias();
            addDependencies_(getDependencies);
        }, 
        // `transferList` for the arguments of the function.
        inputTransferList: (fn) => {
            mustNotHaveStopped();
            mustNotHaveStarted();
            mustNotHaveAlias();
            // Non-TypeScript code argument validation.
            argumentMustBeFunction(fn);
            inputTransferList = fn;
        }, 
        // `transferList` for the result of the function.
        outputTransferList: (fn) => {
            mustNotHaveStopped();
            mustNotHaveStarted();
            mustNotHaveAlias();
            // Non-TypeScript code argument validation.
            argumentMustBeFunction(fn);
            outputTransferList = fn;
        }, 
        // (optional) Enables caching.
        alias(alias_) {
            mustNotHaveStopped();
            mustNotHaveStarted();
            mustNotHaveAlias();
            alias = alias_;
            setInCache(alias, {
                $: [fn, getDependenciesFunctions, inputTransferList, outputTransferList]
            });
        },
        start,
        stop }, createMethods(start, stop, started, stopped, sendToWorker, mustHaveStarted, mustNotHaveStarted, mustNotHaveStopped));
    // Cache accessors for use in `createWorker`.
    const getOtherFromCache = () => {
        if (alias) {
            const properties = getFromCache(alias);
            if (properties) {
                return properties.other;
            }
        }
    };
    const setOtherInCache = (value) => {
        if (alias) {
            setInCache(alias, Object.assign(Object.assign({}, getFromCache(alias)), { other: value }));
        }
    };
    worker = createWorker(
    // Creates a worker in a specific environment such as a web browser or Node.js.
    createWorkerInEnvironment, 
    // This function will be executed in the worker thread.
    // It will be stringified and injected in the worker source code.
    // It must create an input handler function.
    (
    // This function sends output from the worker thread to the main thread.
    sendOutput_) => {
        let inputSentTimestamp = 0;
        let inputReceivedTimestamp = 0;
        const sendOutput = (output) => {
            sendOutput_([inputSentTimestamp, inputReceivedTimestamp, Date.now(), output], outputTransferList(output));
        };
        const inputHandler = createInputHandler(fn, sendOutput);
        return ([inputSentAt, input]) => {
            inputSentTimestamp = inputSentAt;
            inputReceivedTimestamp = Date.now();
            return inputHandler(input);
        };
    }, 
    // This function will be executed in the main thread
    // when an error is received from the worker.
    // Currently, we are in the main thread.
    (error) => {
        if (!stopped) {
            handleError(error);
        }
    }, 
    // This function will be executed in the main thread
    // when output is received from the worker.
    // Currently, we are in the main thread.
    ([inputSentTimestamp, inputReceivedTimestamp, outputTimestamp, output]) => {
        if (!stopped) {
            workerFn.inputLatency = inputReceivedTimestamp - inputSentTimestamp;
            workerFn.outputLatency = Date.now() - outputTimestamp;
            handleOutput(output);
        }
    }, 
    // Caching.
    getOtherFromCache, setOtherInCache);
    return workerFn;
}
const CACHE = {};
